/**
 * 자막 정렬 방향 — 최신이 위냐 아래냐(사용자 취향).
 *
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ **정렬은 순수한 표시(render) 관심사다.**                              │
 * │                                                                       │
 * │ 리듀서의 상태는 이미 키(utt/seq)로 정렬돼 있고, 방향 전환은 그 배열을 │
 * │ 뒤집어 그리는 것 이상을 하지 않는다. 즉:                              │
 * │   · 서버 왕복 없음 — 정렬은 이 브라우저의 취향이지 방의 상태가 아니다 │
 * │   · 스토리지 재구성 없음 — 재접속·재생·히스토리 로드 경로를 건드리지  │
 * │     않는다                                                            │
 * │   · 리듀서 불변식 그대로 — partial in-place 교체(§14), gap 이 이웃    │
 * │     사이에 남는 것, seq 멱등성이 전부 정렬과 무관하게 유지된다        │
 * │                                                                       │
 * │ 나중에 "정렬을 바꾸면 다시 불러와야 하지 않나" 로 이것을 리로드나     │
 * │ 서버 상태로 "최적화" 하지 마라. 그렇게 하면 회의 중 정렬 한 번에      │
 * │ 자막이 통째로 다시 흐르고(§8.3 재생), 읽던 자리를 잃는다.            │
 * └───────────────────────────────────────────────────────────────────────┘
 */

import type { FeedItem } from './reducer'

export type FeedOrder =
  /** 최신이 **위** — 기본값(§14 회의실 프로젝터 용례). */
  | 'newest-first'
  /** 최신이 **아래** — 채팅처럼 읽고 싶은 사용자. */
  | 'newest-last'

export const DEFAULT_ORDER: FeedOrder = 'newest-first'

/** 호스트·뷰어가 **각자** 기억한다 — 한 사람이 두 역할을 오갈 수 있다. */
export function orderStorageKey(role: 'host' | 'viewer'): string {
  return `omnitrans:feed-order:${role}`
}

export function isFeedOrder(value: unknown): value is FeedOrder {
  return value === 'newest-first' || value === 'newest-last'
}

/**
 * 리듀서 상태를 화면 순서로 바꾼다.
 *
 * 리듀서는 **항상 최신 우선**(내림차순)으로 items 를 들고 있다 — 그것이
 * 삽입·supersede·gap 병합 로직의 전제다. 여기서는 그 배열을 건드리지 않고
 * 방향만 정한다. `newest-first` 는 원본을 **그대로** 돌려주므로(복사조차
 * 하지 않는다) 기본 경로에 비용이 0 이다.
 */
export function orderItems(items: FeedItem[], order: FeedOrder): FeedItem[] {
  return order === 'newest-first' ? items : items.slice().reverse()
}

/**
 * "최신" 쪽 끝이 어디인가 — 진행 중(in-flight) 상태 카드가 붙는 자리이자
 * 새 자막이 나타나는 자리다.
 */
export function newestEdge(order: FeedOrder): 'top' | 'bottom' {
  return order === 'newest-first' ? 'top' : 'bottom'
}

/**
 * "과거" 쪽 끝 — 히스토리 페이지네이션(§8.3)이 채우는 자리다.
 * 최신 우선이면 과거는 아래, 반대면 위다.
 */
export function oldestEdge(order: FeedOrder): 'top' | 'bottom' {
  return order === 'newest-first' ? 'bottom' : 'top'
}
